import { cloneElement, type ReactElement, type ReactNode } from "react";
import { cn } from "@/shared/utils/cn";

interface FormFieldProps {
  id: string;
  label: ReactNode;
  description?: ReactNode;
  error?: string;
  optional?: boolean;
  required?: boolean;
  children: ReactElement<{
    id?: string;
    "aria-describedby"?: string;
    "aria-invalid"?: boolean;
    required?: boolean;
  }>;
  className?: string;
}

export function FormField({
  id,
  label,
  description,
  error,
  optional = false,
  required,
  children,
  className,
}: FormFieldProps) {
  const isRequired = required ?? children.props.required;
  const describedBy = [
    children.props["aria-describedby"],
    description ? `${id}-description` : undefined,
    error ? `${id}-error` : undefined,
  ].filter(Boolean).join(" ") || undefined;

  return (
    <div className={cn("min-w-0 space-y-[7px]", className)}>
      <label htmlFor={id} className="flex flex-wrap items-baseline gap-1 text-sm font-semibold text-foreground">
        {label}
        {isRequired && !(typeof label === "string" && label.includes("*")) && <span aria-hidden="true">*</span>}
        {optional && !isRequired && <span className="text-xs font-normal text-muted-foreground">Optional</span>}
      </label>
      {cloneElement(children, {
        id,
        "aria-describedby": describedBy,
        "aria-invalid": error ? true : children.props["aria-invalid"],
        ...(required !== undefined ? { required } : {}),
      })}
      {description && (
        <p id={`${id}-description`} className="text-xs leading-relaxed text-muted-foreground">
          {description}
        </p>
      )}
      {error && (
        <p id={`${id}-error`} role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}
